import axios from 'axios';

/**
 * Axios instance configured for the Email Agent API
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Add auth token if available (future)
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add secret header for desktop app authentication
    // Only add if window.electron.getSecret() is available (Electron mode)
    if (typeof window !== 'undefined' && window.electron && typeof window.electron.getSecret === 'function') {
      try {
        const secret = await window.electron.getSecret();
        if (secret) {
          config.headers['X-Boswell-Secret'] = secret;
        }
      } catch (err) {
        // Gracefully handle missing secret - don't add header
        console.debug('[API] Secret not available, skipping header');
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Return the data directly if success field is true
    if (response.data && response.data.success) {
      return response.data;
    }
    return response.data;
  },
  (error) => {
    // Handle errors
    if (error.response) {
      // Server responded with error status
      const errorData = error.response.data?.error;
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || error.response.data?.message || 'An error occurred';
      console.error('[API Error]', errorMessage);
      return Promise.reject(new Error(errorMessage));
    } else if (error.request) {
      // Request made but no response
      console.error('[API Error] No response from server');
      return Promise.reject(new Error('Could not connect to server'));
    } else {
      // Something else happened
      console.error('[API Error]', error.message);
      return Promise.reject(error);
    }
  }
);

export default api;

// API methods organized by resource
export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
  test: (data) => api.post('/accounts/test', data),
  sync: (id) => api.post(`/accounts/${id}/sync`, {}, { timeout: 120000 }), // 2 minute timeout for sync operations
};

export const emailsAPI = {
  getAll: (params) => api.get('/emails', { params }),
  getById: (id) => api.get(`/emails/${id}`),
  getBody: (id) => api.get(`/emails/${id}/body`),
  update: (id, data) => api.put(`/emails/${id}`, data),
  delete: (id) => api.delete(`/emails/${id}`),
  search: (query, filters) => api.post('/emails/search', { query, ...filters }),
  getStats: (accountId) => api.get('/emails/stats', { params: { account_id: accountId } }),
  bulkUpdate: (emailIds, updates) => api.post('/emails/bulk/update', { email_ids: emailIds, updates }),
  bulkDelete: (emailIds) => api.post('/emails/bulk/delete', { email_ids: emailIds }),
  analyzeBulk: (emailIds) => api.post('/emails/bulk/analyze', { email_ids: emailIds }),
};

export const rulesAPI = {
  getAll: (filters) => api.get('/rules', { params: filters }),
  getById: (id) => api.get(`/rules/${id}`),
  create: (data) => api.post('/rules', data),
  update: (id, data) => api.put(`/rules/${id}`, data),
  delete: (id) => api.delete(`/rules/${id}`),
  reorder: (orderedIds) => api.put('/rules/reorder', { ordered_ids: orderedIds }),
  test: (id, sampleEmails) => api.post(`/rules/${id}/test`, { sample_emails: sampleEmails }),
};

export const systemAPI = {
  getStatus: () => api.get('/system/status'),
  start: () => api.post('/system/start'),
  stop: () => api.post('/system/stop'),
  getLogs: (limit) => api.get('/system/logs', { params: { limit } }),
  health: () => api.get('/system/health'),
};

export const authAPI = {
  register: (email, password) => api.post('/auth/register', { email, password }),
  login: (email, password, rememberMe = false) => api.post('/auth/login', { email, password, remember_me: rememberMe }),
  me: () => api.get('/auth/me'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
};

export const configAPI = {
  get: () => api.get('/config'),
  update: (data) => api.put('/config', data),
  isConfigured: () => api.get('/config/is-configured'),
};

export const aiInstructionsAPI = {
  getAll: (params) => api.get('/ai-instructions', { params }),
  getById: (id) => api.get(`/ai-instructions/${id}`),
  create: (data) => api.post('/ai-instructions', data),
  update: (id, data) => api.put(`/ai-instructions/${id}`, data),
  delete: (id) => api.delete(`/ai-instructions/${id}`),
};

export const boswellAPI = {
  chat: (message, conversationHistory) => api.post('/boswell/chat', {
    message,
    conversation_history: conversationHistory
  }),
};

export const pluginsAPI = {
  getAll: () => api.get('/plugins'),
  getStatus: () => api.get('/plugins/status'),
  getStats: () => api.get('/plugins/stats'),
  getData: (emailId) => api.get(`/emails/${emailId}/plugins`),
  enable: (name) => api.post(`/plugins/${name}/enable`),
  disable: (name) => api.post(`/plugins/${name}/disable`),
  reload: () => api.post('/plugins/reload'),
};
